import React from "react"
import {
  PixelRatio,
  Text as RNText,
  TextProps as RNTextProps,
  StyleProp,
  StyleSheet,
  TextStyle,
} from "react-native"

import { useAppTheme } from "app/hooks/useAppTheme"
import { typography } from "app/theme/typography"
import i18n from "i18n-js"

import { isRTL, translate, TxKeyPath } from "src/i18n"
import type { ThemedStyle, ThemedStyleArray } from "app/theme"

type Sizes = keyof typeof $sizeStyles
type Weights = keyof typeof typography.primary
type Presets = "default" | "bold" | "heading" | "subheading" | "formLabel" | "formHelper"

export interface TextProps extends RNTextProps {
  /**
   * Text which is looked up via i18n.
   */
  tx?: TxKeyPath
  /**
   * The text to display if not using `tx` or nested components.
   */
  text?: string
  /**
   * Optional options to pass to i18n. Useful for interpolation
   * as well as explicitly setting locale or translation fallbacks.
   */
  txOptions?: i18n.TranslateOptions
  /**
   * An optional style override useful for padding & margin.
   */
  style?: StyleProp<TextStyle>
  /**
   * One of the different types of text presets.
   */
  preset?: Presets
  /**
   * Text weight modifier.
   */
  weight?: Weights
  /**
   * Text size modifier.
   */
  size?: Sizes
  /**
   * Children components.
   */
  children?: React.ReactNode
}

/**
 * For your text displaying needs.
 * This component is a HOC over the built-in React Native one.
 * @see [Documentation and Examples]{@link https://docs.infinite.red/ignite-cli/boilerplate/components/Text/}
 * @param {TextProps} props - The props for the `Text` component.
 * @returns {JSX.Element} The rendered `Text` component.
 */
export function Text(props: TextProps) {
  const { weight, size, tx, txOptions, text, children, style: $styleOverride, ...rest } = props
  const { themed } = useAppTheme()
  const fontScale = PixelRatio.getFontScale()

  const i18nText = tx && translate(tx, txOptions)
  const content = i18nText || text || children

  const preset: Presets = props.preset ?? "default"

  // Determine the effective fontSize so the lineHeight stays proportional even
  // when a call-site overrides fontSize (e.g. $channelName with fontSize 28).
  // StyleSheet.flatten merges the full style array into a single object so we
  // can read the resolved value. Priority: explicit style override > size prop >
  // preset's own fontSize > sm base size (16).
  const flatOverride = StyleSheet.flatten($styleOverride) as TextStyle | null
  const effectiveFontSize =
    (flatOverride?.fontSize as number | undefined) ??
    (size ? $sizeStyles[size].fontSize : undefined) ??
    $presetFontSizes[preset]

  // Mirrors the ButtonCluster/MessageBubble pattern: explicit fontScale-aware
  // lineHeight so Yoga (Fabric) recomputes layout when fontScale changes.
  // Placed before $styleOverride so callers can still supply their own.
  const scaledLineHeight = Math.ceil(effectiveFontSize * fontScale * 1.35)

  const $styles: StyleProp<TextStyle> = [
    $rtlStyle,
    themed($presets[preset]),
    weight && $fontWeightStyles[weight],
    size && $sizeStyles[size],
    { lineHeight: scaledLineHeight },
    $styleOverride,
  ]

  return (
    <RNText {...rest} style={$styles}>
      {content}
    </RNText>
  )
}

const $sizeStyles = {
  xxl: { fontSize: 36, lineHeight: 44 } satisfies TextStyle,
  xl: { fontSize: 24, lineHeight: 34 } satisfies TextStyle,
  lg: { fontSize: 20, lineHeight: 32 } satisfies TextStyle,
  md: { fontSize: 18, lineHeight: 26 } satisfies TextStyle,
  sm: { fontSize: 16, lineHeight: 24 } satisfies TextStyle,
  xs: { fontSize: 14, lineHeight: 21 } satisfies TextStyle,
  xxs: { fontSize: 12, lineHeight: 18 } satisfies TextStyle,
}

const $fontWeightStyles = Object.entries(typography.primary).reduce((acc, [weight, fontFamily]) => {
  return { ...acc, [weight]: { fontFamily } }
}, {}) as Record<Weights, TextStyle>

const $baseStyle: ThemedStyle<TextStyle> = (theme) => ({
  fontSize: $sizeStyles.sm.fontSize,
  ...$fontWeightStyles.normal,
  color: theme.colors.text,
})

/** Canonical fontSize for each preset, used to compute the scaled lineHeight. */
const $presetFontSizes: Record<Presets, number> = {
  default: $sizeStyles.sm.fontSize,
  bold: $sizeStyles.sm.fontSize,
  heading: $sizeStyles.xxl.fontSize,
  subheading: $sizeStyles.lg.fontSize,
  formLabel: $sizeStyles.sm.fontSize,
  formHelper: $sizeStyles.sm.fontSize,
}

const $presets: Record<Presets, ThemedStyleArray<TextStyle>> = {
  default: [$baseStyle],
  bold: [$baseStyle, { ...$fontWeightStyles.bold }],
  heading: [
    $baseStyle,
    {
      ...$sizeStyles.xxl,
      ...$fontWeightStyles.bold,
    },
  ],
  subheading: [$baseStyle, { ...$sizeStyles.lg, ...$fontWeightStyles.medium }],
  formLabel: [$baseStyle, { ...$fontWeightStyles.medium }],
  formHelper: [$baseStyle, { ...$sizeStyles.sm, ...$fontWeightStyles.normal }],
}
const $rtlStyle: TextStyle = isRTL ? { writingDirection: "rtl" } : {}
